export type ParsedTpmM =
  | { readonly kind: 'empty' }
  | { readonly kind: 'value'; readonly value: number }
  | { readonly kind: 'invalid' };

const TPM_M_PATTERN = /^\d+(\.\d{1,3})?$/;

function trimTrailingZeros(raw: string): string {
  return raw.replace(/\.?0+$/, '');
}

/** Parse a TPM field in millions of tokens. Empty inherits; 0 / negatives / extra decimals are invalid. */
export function parseTpmM(raw: string): ParsedTpmM {
  const trimmed = raw.trim();
  if (trimmed === '') return { kind: 'empty' };
  if (!TPM_M_PATTERN.test(trimmed)) return { kind: 'invalid' };
  const millions = Number(trimmed);
  if (!Number.isFinite(millions) || millions <= 0) return { kind: 'invalid' };
  const tokens = Math.round(millions * 1_000_000);
  if (tokens < 1) return { kind: 'invalid' };
  return { kind: 'value', value: tokens };
}

/**
 * If the input still equals the formatted original, keep the stored token
 * integer. `formatTpmMInput` is a 1000-token quantizer (3 M-decimals), so
 * re-parsing an untouched field would rewrite 123456 → 123000.
 */
export function parseTpmMPreserving(raw: string, original: number | null | undefined): ParsedTpmM {
  if (typeof original === 'number' && raw.trim() === formatTpmMInput(original)) {
    return { kind: 'value', value: original };
  }
  return parseTpmM(raw);
}

/** Display stored TPM tokens as millions. Sub-10k tokens use 3 decimals so 1000 → 0.001 M. */
export function formatTpmM(tokens: number): string {
  const decimals = tokens < 10_000 ? 3 : 2;
  return `${trimTrailingZeros((tokens / 1_000_000).toFixed(decimals))} M`;
}

export function formatTpmCap(tokens: number | null | undefined, unlimited: string): string {
  return typeof tokens === 'number' ? formatTpmM(tokens) : unlimited;
}

/** Load stored TPM tokens into an input (millions, up to 3 decimals, no suffix). */
export function formatTpmMInput(tokens: number | null | undefined): string {
  if (typeof tokens !== 'number') return '';
  return trimTrailingZeros((tokens / 1_000_000).toFixed(3));
}
